/* DripStageEditor — In-dashboard live editor for the 3-stage abandoned
 * cart drip. Backed by:
 *   GET    /api/store/admin/drip/stages        — list active config + defaults
 *   PUT    /api/store/admin/drip/stages/{n}    — publish overrides
 *   DELETE /api/store/admin/drip/stages/{n}    — reset to in-code default
 *
 * Marketing team can A/B-test subject lines, coupon values, and stage
 * timings without a code deploy. Stage number + code_prefix are
 * intentionally non-editable so the revenue-attribution join in
 * /admin/drip/performance keeps working across config changes.
 *
 * Mounted INSIDE DripPerformancePanel as a collapsible "Edit stages"
 * drawer so admins always see the metric → editor relationship.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Edit3, RotateCcw, Save, X, ChevronDown, AlertTriangle, CheckCircle2, Sparkles,
} from 'lucide-react';

const API = `${(process.env.REACT_APP_API_BASE_URL || process.env.CRA_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;
const TOKEN_KEY = 'zhelta_store_token';
function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
async function authFetch(path, options = {}) {
  const tok = getToken();
  const headers = { ...(options.headers || {}) };
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  return fetch(`${API}${path}`, { ...options, headers });
}

const STAGE_TONE = {
  1: 'border-emerald-200 bg-emerald-50/50',
  2: 'border-amber-200 bg-amber-50/50',
  3: 'border-rose-200 bg-rose-50/50',
};
const STAGE_LABEL = {
  1: 'text-emerald-700',
  2: 'text-amber-700',
  3: 'text-rose-700',
};

function diffsFromDefault(active, defaults) {
  if (!active || !defaults) return [];
  const fields = ['hours', 'coupon_type', 'coupon_value', 'coupon_hours', 'subject'];
  return fields.filter((f) => active[f] !== defaults[f]);
}

function StageForm({ active, defaultCfg, onSaved }) {
  const [draft, setDraft] = useState(() => ({
    hours: active.hours,
    subject: active.subject,
    coupon_type: active.coupon_type,
    coupon_value: active.coupon_value,
    coupon_hours: active.coupon_hours,
  }));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const overridden = useMemo(() => diffsFromDefault(active, defaultCfg), [active, defaultCfg]);

  const dirty = (
    draft.hours !== active.hours
    || draft.subject !== active.subject
    || draft.coupon_type !== active.coupon_type
    || Number(draft.coupon_value) !== Number(active.coupon_value)
    || draft.coupon_hours !== active.coupon_hours
  );

  const publish = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await authFetch(`/store/admin/drip/stages/${active.stage}`, {
        method: 'PUT',
        body: JSON.stringify(draft),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setMsg('Published ✓');
      onSaved();
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      setMsg(e.message || 'Publish failed');
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (overridden.length === 0) return;
    setBusy(true); setMsg('');
    try {
      const r = await authFetch(`/store/admin/drip/stages/${active.stage}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMsg('Reset to default ✓');
      onSaved();
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      setMsg(e.message || 'Reset failed');
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid={`drip-editor-stage-${active.stage}`}
      className={`p-4 rounded-xl border ${STAGE_TONE[active.stage]}`}
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap mb-3">
        <div>
          <span className={`text-[10px] font-bold uppercase tracking-[0.22em] ${STAGE_LABEL[active.stage]}`}>
            Stage {active.stage} · prefix {active.code_prefix}
          </span>
          {overridden.length > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#c9a227]/15 text-[#a8871a] text-[9.5px] font-extrabold uppercase tracking-[0.14em]">
              <Sparkles className="w-2.5 h-2.5" /> {overridden.length} override{overridden.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {overridden.length > 0 && (
          <button
            type="button"
            onClick={reset}
            disabled={busy}
            data-testid={`drip-editor-reset-${active.stage}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white text-gray-600 border border-gray-200 hover:border-rose-300 hover:text-rose-700 disabled:opacity-40 transition-colors"
            title={`Reset stage ${active.stage} to default`}
          >
            <RotateCcw className="w-3 h-3" /> Reset to default
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12px]">
        <Field label="Subject line" hint={`Default: "${defaultCfg?.subject || ''}"`} fullSpan>
          <input
            type="text"
            value={draft.subject}
            maxLength={200}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            data-testid={`drip-editor-subject-${active.stage}`}
            className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227] bg-white"
          />
        </Field>

        <Field label="Send delay (hours after cart abandoned)" hint={`Default: ${defaultCfg?.hours}h`}>
          <input
            type="number"
            min={1} max={720}
            value={draft.hours}
            onChange={(e) => setDraft({ ...draft, hours: parseInt(e.target.value || '0', 10) })}
            data-testid={`drip-editor-hours-${active.stage}`}
            className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227] bg-white"
          />
        </Field>

        <Field label="Coupon validity (hours)" hint={`Default: ${defaultCfg?.coupon_hours}h`}>
          <input
            type="number"
            min={1} max={720}
            value={draft.coupon_hours}
            onChange={(e) => setDraft({ ...draft, coupon_hours: parseInt(e.target.value || '0', 10) })}
            data-testid={`drip-editor-coupon-hours-${active.stage}`}
            className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227] bg-white"
          />
        </Field>

        <Field label="Coupon type" hint={`Default: ${defaultCfg?.coupon_type}`}>
          <select
            value={draft.coupon_type}
            onChange={(e) => setDraft({ ...draft, coupon_type: e.target.value })}
            data-testid={`drip-editor-coupon-type-${active.stage}`}
            className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227] bg-white"
          >
            <option value="free_shipping">Free shipping</option>
            <option value="percent">% off entire cart</option>
            <option value="amount">Flat $ off</option>
          </select>
        </Field>

        <Field label={draft.coupon_type === 'free_shipping' ? 'Coupon value (ignored for free shipping)' : draft.coupon_type === 'percent' ? 'Coupon value (% off)' : 'Coupon value ($ off)'} hint={`Default: ${defaultCfg?.coupon_value}`}>
          <input
            type="number"
            min={0} max={1000}
            step={draft.coupon_type === 'percent' ? 1 : 0.01}
            value={draft.coupon_value}
            onChange={(e) => setDraft({ ...draft, coupon_value: parseFloat(e.target.value || '0') })}
            disabled={draft.coupon_type === 'free_shipping'}
            data-testid={`drip-editor-coupon-value-${active.stage}`}
            className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227] bg-white disabled:bg-gray-50 disabled:text-gray-400"
          />
        </Field>
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        {msg && (
          <span className={`text-[11px] font-bold ${msg.includes('✓') ? 'text-emerald-700' : 'text-rose-700'}`}>
            {msg.includes('✓') ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <AlertTriangle className="w-3 h-3 inline mr-1" />}
            {msg}
          </span>
        )}
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={publish}
          data-testid={`drip-editor-publish-${active.stage}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-zinc-950 font-bold uppercase tracking-wider text-[10.5px] disabled:opacity-40 hover:opacity-90 transition-opacity"
          style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
        >
          <Save className="w-3 h-3" />
          {busy ? 'Publishing…' : 'Publish'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children, fullSpan }) {
  return (
    <div className={`flex flex-col gap-1 ${fullSpan ? 'md:col-span-2' : ''}`}>
      <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600">{label}</label>
      {children}
      {hint && <span className="text-[10px] text-gray-400 truncate" title={hint}>{hint}</span>}
    </div>
  );
}

export default function DripStageEditor({ defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await authFetch('/store/admin/drip/stages');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (expanded && !data) load(); }, [expanded, data, load]);

  const totalOverrides = useMemo(() => {
    if (!data) return 0;
    return data.stages.reduce((acc, s) => {
      const def = data.defaults.find((d) => d.stage === s.stage);
      return acc + diffsFromDefault(s, def).length;
    }, 0);
  }, [data]);

  return (
    <div className="border-t border-gray-100" data-testid="drip-stage-editor">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        data-testid="drip-stage-editor-toggle"
        className="w-full px-6 py-3 flex items-center justify-between gap-2 hover:bg-gray-50 transition-colors text-left"
      >
        <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-gray-700">
          <Edit3 className="w-3.5 h-3.5 text-[#c9a227]" />
          Edit drip stages
          {totalOverrides > 0 && (
            <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#c9a227]/15 text-[#a8871a] text-[9.5px] font-extrabold tracking-[0.14em]">
              <Sparkles className="w-2.5 h-2.5" /> {totalOverrides} live override{totalOverrides > 1 ? 's' : ''}
            </span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-6 pb-5 pt-2 flex flex-col gap-3" data-testid="drip-stage-editor-body">
          <div className="text-[11.5px] text-gray-500 leading-relaxed">
            <strong>Live config.</strong> Edits apply to the next abandoned-cart sweep — no deploy needed.
            Stage number and coupon code prefix are immutable so revenue attribution stays consistent.
            <span className="ml-1 text-[#a8871a] font-semibold"><X className="w-2.5 h-2.5 inline" /> Reset to default</span> wipes overrides for one stage.
          </div>

          {err && (
            <div className="px-3 py-2 rounded-md text-[11.5px] bg-rose-50 text-rose-800 border border-rose-200">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {err}
            </div>
          )}

          {loading && !data && <div className="text-[12px] text-gray-500">Loading stages…</div>}

          {data?.stages?.map((s) => {
            const def = data.defaults.find((d) => d.stage === s.stage);
            return <StageForm key={s.stage} active={s} defaultCfg={def} onSaved={load} />;
          })}
        </div>
      )}
    </div>
  );
}
